
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserManagement } from "@/components/settings/UserManagement";
import { ImportAuditLog } from "@/components/settings/ImportAuditLog";
import { DataQualityCenter } from "@/components/settings/DataQualityCenter";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { KolPic } from "@/types/table";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { RefreshCw, Users, Database, Settings as SettingsIcon, ClipboardCheck } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

const Settings = () => {
  const [users, setUsers] = useState<KolPic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const userAccess = localStorage.getItem('dashboard_user_access');
  const hasFullAccess = userAccess === 'full-access';
  const hasLimitedAccess = userAccess === 'limited';
  
  useEffect(() => {
    // Allow both full-access and limited users, but redirect others
    if (!hasFullAccess && !hasLimitedAccess) {
      toast({
        title: "Access Denied",
        description: "You need access to view the Settings page",
        variant: "destructive"
      });
      
      navigate('/global-reach', { replace: true });
    }
  }, [userAccess, toast, navigate, hasFullAccess, hasLimitedAccess]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('kol_pic')
        .select('*')
        .order('name', { ascending: true });
        
      if (error) {
        console.error("Error fetching users:", error);
        toast({
          title: "Error fetching users",
          description: error.message,
          variant: "destructive"
        });
        return;
      }
      
      if (data) {
        setUsers(data as KolPic[]);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
      toast({
        title: "Unexpected error",
        description: "Failed to load user data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (hasFullAccess) {
      fetchUsers();
    }
  }, [hasFullAccess]);

  // Redirect users without any access
  if (!hasFullAccess && !hasLimitedAccess) {
    return null;
  }

  // Determine default tab based on user access
  const defaultTab = hasLimitedAccess ? "quality" : "users";

  return (
    <div className="w-full min-h-screen bg-background">
      <div className="w-full max-w-none mx-auto p-6 space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <SettingsIcon className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-dashboard-text-primary">Settings</h1>
            <p className="text-muted-foreground">
              {hasFullAccess 
                ? "Manage users, audit logs, and data quality" 
                : "Analyze and fix data quality issues"
              }
            </p>
          </div>
        </div>
        
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className={`grid w-full ${hasFullAccess ? 'grid-cols-3 lg:w-[600px]' : 'grid-cols-1 lg:w-[200px]'}`}>
            {hasFullAccess && (
              <TabsTrigger value="users" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                User Management
              </TabsTrigger>
            )}
            {hasFullAccess && (
              <TabsTrigger value="audit" className="flex items-center gap-2"> 
                <Database className="h-4 w-4" />
                Audit & Rollback
              </TabsTrigger>
            )}
            <TabsTrigger value="quality" className="flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" />
              Data Quality Center
            </TabsTrigger>
          </TabsList>
          
          {hasFullAccess && (
            <TabsContent value="users" className="w-full mt-6">
              <Card className="w-full bg-card border border-border">
                <CardHeader className="border-b border-border pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-xl">
                        <Users className="h-5 w-5" />
                        User Management
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        Create, manage, and configure user accounts with appropriate access levels
                      </p>
                    </div>
                    <Button
                      onClick={fetchUsers}
                      disabled={isLoading}
                      variant="outline"
                      className="border-primary text-primary hover:bg-primary hover:text-white"
                    >
                      <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[70vh] w-full">
                    <div className="p-6">
                      <UserManagement 
                        users={users} 
                        isLoading={isLoading} 
                        onRefresh={fetchUsers} 
                      />
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {hasFullAccess && (
            <TabsContent value="audit" className="w-full mt-6">
              <Card className="w-full bg-card border border-border">
                <CardHeader className="border-b border-border pb-4">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Database className="h-5 w-5" />
                    Audit & Rollback
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    View import history and rollback operations
                  </p>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[70vh] w-full">
                    <div className="p-6">
                      <ImportAuditLog />
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          <TabsContent value="quality" className="w-full mt-6">
            <Card className="w-full bg-card border border-border">
              <CardHeader className="border-b border-border pb-4">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <ClipboardCheck className="h-5 w-5" />
                  Data Quality Center
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Analyze and fix data quality issues in KOL records
                </p>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[70vh] w-full">
                  <div className="p-6">
                    <DataQualityCenter />
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Settings;
